import React, { Dispatch } from "react";
import { CognitoUser } from "../models/CognitoUser";
import { ActionType } from "./actions";
import { v4 as uuid } from "uuid";
import { ILivestockUser } from "../models/ILivestockUser";
import { Business } from "../models/Business";
import { Gift } from "../models/Gift";
import { DeferredLoadingFlags } from "../util";
import { FirebaseObject } from "../models/FirebasePostObject";
import { Customer } from "../models/Customer";
import logger from "../config/Logging";
import { Holding } from "../models/Holding";

// TODO
// Enable logging in development
// const logging = process.env.NODE_ENV === "development";

export type Action = {
  type: ActionType;
  payload?: any;
};

export type DispatchThunk<A = Action, AR = any, R = any> = (
  action:
    | Action
    | ((dispatch: Dispatch<A>, state: State) => Promise<AR>)
    | ((dispatch: DispatchThunk, state: State) => Promise<AR>)
) => Promise<R>;

export type State = {
  user?: CognitoUser;
  authenticating: boolean;
  fetching: boolean;
  ilivestockUser?: ILivestockUser;
  businesses?: FirebaseObject<Business>[];
  missingBusinesses?: Business[];
  gifts?: Gift[];
  holdings?: FirebaseObject<Holding>[];
  loading: Set<DeferredLoadingFlags>;
};

export type Store = {
  state: State;
  dispatch: DispatchThunk;
};

const preInitializeStore: Store = {
  state: {
    user: undefined,
    authenticating: true,
    fetching: true,
    loading: new Set([DeferredLoadingFlags.SILENT_AUTH]),
  },
  dispatch: () => {
    throw new Error("Dispatch not injected");
  },
};

const store = React.createContext<Store>(preInitializeStore);

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.SET_USER:
      return { ...state, user: action.payload };
    case ActionType.SET_AUTHENTICATING:
      return { ...state, authenticating: action.payload };
    case ActionType.SET_ILIVESTOCK_USER:
      return { ...state, ilivestockUser: action.payload };
    case ActionType.SET_BUSINESSES:
      return { ...state, businesses: action.payload };
    case ActionType.SET_BUSINESS: {
      const businesses = state.businesses ? [...state.businesses] : [];
      const exists = businesses.find((business) => {
        if (
          business.data.attributes.code === action.payload.data.attributes.code
        )
          return business;
        return null;
      });
      if (exists) {
        const index = businesses.indexOf(exists);
        businesses.splice(index, 1, action.payload);
      } else {
        businesses.push(action.payload);
      }
      return { ...state, businesses };
    }
    case ActionType.SET_MISSING_BUSINESSES:
      return { ...state, missingBusinesses: action.payload };

    case ActionType.SET_GIFTS:
      return { ...state, gifts: action.payload };

    case ActionType.SET_HOLDINGS:
      return { ...state, holdings: action.payload };

    case ActionType.MERGE_BUSINESSES:
      return {
        ...state,
        businesses: action.payload,
      };
    case ActionType.SET_FETCHING_USER_DATA:
      return { ...state, fetching: action.payload };
    case ActionType.REMOVE_DEFERRED_LOADING_FLAG: {
      const newSet = new Set(state.loading);
      action.payload.forEach((flag: DeferredLoadingFlags) => {
        newSet.delete(flag);
      });
      return { ...state, loading: newSet };
    }
    case ActionType.ADD_DEFERRED_LOADING_FLAG: {
      if (state.loading.has(action.payload)) return state;
      const newSet = new Set(state.loading);
      action.payload.forEach((flag: DeferredLoadingFlags) => {
        newSet.add(flag);
      });
      return { ...state, loading: newSet };
    }
    case ActionType.RESET:
      return {
        authenticating: false,
        fetching: false,
        loading: new Set(),
      };
    default:
      return state;
  }
};

const rootReducer = (store: Store, action: Action): Store => {
  const newState: State = reducer(store.state, action);
  console.groupCollapsed("State updated");
  console.log("%cOld state: ", "color: red", store.state);
  console.log("%cNew state: ", "color: green", newState);
  console.groupEnd();

  store.state = newState;
  return { ...store };
};

const StoreContextProvider: React.FC<{ initialState?: State }> = ({
  children,
  initialState,
}) => {
  /**
   * We wrap the reducer in a function to remove the store from the actual reducer call
   * This allows simplification of the reducer itself, as it's only dealing with
   * pure state
   */
  const [s, d] = React.useReducer(rootReducer, {
    ...preInitializeStore,
    state: initialState || preInitializeStore.state,
  });

  const dispatch = (action: Action) => {
    const msg = {
      action: action.type,
      payload: action.payload,
    };
    console.log("Dispatch -", msg);
    d(action);
  };

  const dispatchAsync = async (
    action:
      | Action
      | ((dispatch: Dispatch<Action>, state: State) => Promise<any>)
      | ((dispatch: DispatchThunk, state: State) => Promise<any>)
  ) => {
    if (typeof action !== "function") {
      dispatch(action);
      return Promise.resolve();
    }
    const eventId = uuid();
    const msg = {
      action: action.name,
      status: "Pending",
      uuid: eventId,
    };
    console.log(`Dispatch async -`, msg);

    try {
      const value = await action(dispatchAsync, s.state);
      const msg = {
        action: action.name,
        uuid: eventId,
        status: "Complete",
      };
      console.log(`Dispatch async -`, msg);

      return value;
    } catch (err) {
      const msg = {
        action: action.name,
        uuid: eventId,
        status: "Failed",
        error: err,
      };
      console.log(`Dispatch async -`, msg);

      throw err;
    }
  };

  return (
    <store.Provider
      value={{
        ...s,
        dispatch: dispatchAsync,
      }}
    >
      {children}
    </store.Provider>
  );
};

export { store };
export const StoreProvider = StoreContextProvider;
export const StoreConsumer = store.Consumer;
